import React, { useRef } from "react";
import { Col, Row } from "react-bootstrap";
import upload from "../../assets/upload.svg";
import MinusAdjustment from "../../assets/max-adjustment.svg";
import MinAdjustment from "../../assets/add-icon.svg";
import { isEmpty } from "lodash";

interface Attachment {
  imageData: string;
  imageType: string;
  updateStatus: boolean;
}

interface RowData {
  subtitle: string;
  attachment: Attachment[];
  errorMessage: string;
  successMessage: string;
  pdfName: string;
}

interface PdfAddProps {
  rows: RowData[];
  PdfMainTitle: any,
  updateRow: (index: number, newData: Partial<RowData>) => void;
  handleAddRow: () => void;
  handleRemoveRow: (index: number, id: any) => void;
  handlePDFClose: (index: number) => void;
  onChange: any;
}

const PdfAdd: React.FC<PdfAddProps> = ({
  rows,
  PdfMainTitle,
  updateRow,
  handleAddRow,
  handleRemoveRow,
  handlePDFClose,
  onChange,
}) => {
  // Create refs for file inputs
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Function to check if file is PDF
  const isPdfFile = (file: File): boolean => {
    return (
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    );
  };

  // Function to process the file
  const processFile = (file: File, rowIndex: number) => {
    // First check if it's a PDF
    if (!isPdfFile(file)) {
      updateRow(rowIndex, {
        errorMessage: "Only PDF files are supported. Please select a PDF file.",
        successMessage: "",
      });
      return;
    }

    // If it's a PDF, proceed with conversion
    const reader = new FileReader();

    reader.onload = (e) => {
      const base64data = e.target?.result as string;
      const type = file.type.replace("application/", "");
      const cleanBase64 = base64data.replace(/^data:application\/pdf;base64,/, '');

      // Update the attachment array
      const newAttachment = [
        ...(rows[rowIndex].attachment || []),
        {
          imageData: type == 'pdf' ? cleanBase64 : base64data,
          imageType: type,
          updateStatus: true,
        },
      ];

      // Update the row with success
      updateRow(rowIndex, {
        attachment: newAttachment,
        successMessage: "PDF uploaded successfully!",
        errorMessage: "",
      });
    };

    reader.onerror = () => {
      updateRow(rowIndex, {
        errorMessage: "Error reading the file. Please try again.",
        successMessage: "",
      });
    };

    // Start reading the file
    reader.readAsDataURL(file);
  };

  // Handle file selection from file dialog
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      processFile(files[0], rowIndex);
    }

    // Reset the input value to allow selecting the same file again
    if (event.target) {
      event.target.value = "";
    }
  };

  // Handle click on choose file
  const handleChooseFile = (rowIndex: number) => {
    fileInputRefs.current[rowIndex]?.click();
  };

  // Handle drag and drop
  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>,
    rowIndex: number
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;

    if (files && files.length > 0) {
      processFile(files[0], rowIndex);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="pdf-upload-container">
      <Row>
        <Col md="6" lg="6" className="mb-3">
          <input
            type="text"
            className="form-control bgborder"
            placeholder="Main Title"
            minLength={2}
            // required={isMainTitleRequired}
            onChange={onChange}
            value={PdfMainTitle}
            name='PdfMainTitle'
          />
        </Col>
      </Row>
      <Row>
        {rows.map((row: any, index) => (
          <React.Fragment key={index}>

            <Col md="5" className="flexalignitemcenter mt-5">
              <input
                type="text"
                className="form-control bgborder"
                placeholder="Enter Sub Title"
                minLength={2}
                maxLength={450}
                value={row.subtitle}
                required={!!PdfMainTitle}
                onChange={(e) => {
                  const sanitizedValue = e.target.value.replace(/ {2,}/g, ' ');  // Removes all spaces
                  updateRow(index, { subtitle: sanitizedValue });
                }}
              />
            </Col>
            <Col md="5" lg="5" className="flexalignitemcenter">
              {isEmpty(row.pdfName) && (

                <div
                  className="file-upload"
                  onDrop={(e) => handleDrop(e, index)}
                  onDragOver={handleDragOver}

                >
                  <input
                    ref={(el) => (fileInputRefs.current[index] = el)}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => handleFileChange(e, index)}
                    required={!!row.subtitle && !row.successMessage}
                  />


                  <div
                    onClick={() => handleChooseFile(index)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="upload-icon">
                      <img
                        src={upload}
                        alt="Upload Icon"
                        style={{
                          width: "40px",
                          height: "40px",
                          marginBottom: "10px",
                        }}
                      />
                    </div>
                    <div className="upload-text">
                      <p>
                        Drag and Drop file here or{" "}
                        <span
                        >
                          Choose file
                        </span>
                      </p>
                      <p
                        className="file-info"
                        style={{ fontSize: "0.9em", color: "#666" }}
                      >
                        Supported format: PDF only.
                      </p>
                    </div>
                  </div>

                  {/* Error Message */}
                  {row.errorMessage && (
                    <div
                      className="error-message"
                      style={{
                        color: "#dc3545",
                        marginTop: "10px",
                        fontSize: "0.9em",
                      }}
                    >
                      {row.errorMessage}
                    </div>
                  )}

                  {/* Success Message */}
                  {row.successMessage && (
                    <div
                      className="success-message"
                      style={{
                        color: "#28a745",
                        marginTop: "10px",
                        fontSize: "0.9em",
                      }}
                    >
                      {row.successMessage}
                    </div>
                  )}
                </div>
              )}
              {row.pdfName && (
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                  }}
                >
                  <a href={row.pdfName} target="_blank">Preview PDF</a>
                  <span
                    style={{

                      cursor: "pointer",
                      fontSize: "18px",
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      boxShadow: "0 0 5px rgba(0,0,0,0.3)",
                    }}
                    onClick={() => handlePDFClose(index)}
                  >
                    &times;
                  </span>
                </div>
              )}
            </Col>
            <br />
            <Col md="2" lg="2" className="flexalignitemstart">
              <img
                style={{ width: "28px", cursor: "pointer" }}
                src={MinusAdjustment}
                alt="Remove icon"
                onClick={() => handleRemoveRow(index, row.id)}
              />
            </Col>
          </React.Fragment>
        ))}
        <Col md="2" lg="2" className="flexalignitemstart">
          <div
            className="more-btn d-flex align-items-center justify-content-center"
            onClick={handleAddRow}
            style={{ cursor: "pointer" }}
          >
            <img
              style={{ marginRight: "10px", width: "12px", height: "12px" }}
              src={MinAdjustment}
              alt="add-icon"
            />
            Add more
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PdfAdd;
